// scheduleSync.js — shared "Sync your calendar" eligibility + trigger.
//
// CORA: the backend pulls the synced schedule (getSchedule({})) — works on any
// surface. WebPT, Raintree, Stride and Prompt: we scrape the appointments from
// the EMR browser tab (getScheduleFromEMR), which only exists in the CX
// extension. WebPT stays behind the sync_cal flag; the rest are on unless
// sync_cal is explicitly set to false for the user.
import {
  getPageUrl,
  getScheduleFromEMR,
} from '../EMR_Integration/react_webInjector.ts';

const WEBPT_SCHEDULE_EMRS = [
  'WebPT',
  'WebPT 1',
  'WebPT 1.0',
  'WebPT 2',
  'WebPT 2.0',
  'WebPT1',
  'WebPT1.0',
  'WebPT2',
  'WebPT2.0',
];

const RAINTREE_SCHEDULE_EMRS = ['Raintree'];
const STRIDE_SCHEDULE_EMRS = ['StrideThera', 'Stride'];
const PROMPT_SCHEDULE_EMRS = ['Prompt'];
// Read off whichever EMR tab is open, no flag.
const TAB_SCHEDULE_EMRS = [
  ...RAINTREE_SCHEDULE_EMRS,
  ...STRIDE_SCHEDULE_EMRS,
  ...PROMPT_SCHEDULE_EMRS,
];

const PROVIDED_SCHEDULE_EMRS = [
  ...WEBPT_SCHEDULE_EMRS,
  ...TAB_SCHEDULE_EMRS,
];

// CORA is our backend value; "Cora Health" is the frontend EMR-picker label.
const SYNC_CAL_EMRS = ['CORA', 'Cora Health', ...PROVIDED_SCHEDULE_EMRS];

export const usesProvidedScheduleSync = (emr) =>
  PROVIDED_SCHEDULE_EMRS.includes(emr);
export const usesTabScheduleSync = (emr) => TAB_SCHEDULE_EMRS.includes(emr);
export const showSyncCal = (emr) => SYNC_CAL_EMRS.includes(emr);

export const getWebPTEMRFromUrl = (url) => {
  const u = String(url || '').toLowerCase();
  if (u.includes('emr.webpt.com')) return 'WebPT 2.0';
  if (u.includes('app.webpt.com')) return 'WebPT 1.0';
  if (u.includes('raintreeinc.com')) return 'Raintree';
  if (u.includes('stridethera.com')) return 'StrideThera';
  if (u.includes('promptemr.com')) return 'Prompt';
  return null;
};

const isEmbeddedFrame = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

/**
 * Whether to show the Sync Cal button. CORA is available everywhere; Raintree,
 * Stride and Prompt unless sync_cal is set false, WebPT behind the sync_cal flag — only in the CX,
 * which is what can reach the EMR tab.
 */
export const canSyncCal = ({
  emr,
  syncCalEnabled,
  syncCalOff,
  currentPageEMR,
  platform,
}) => {
  const isMobile = platform === 'ios' || platform === 'android';
  const canScrape = !isMobile && !isEmbeddedFrame();

  // Raintree/Stride/Prompt: the open EMR tab decides, not the account's EMR setting —
  // the schedule we read is the one on screen. No tab, no button.
  if (usesTabScheduleSync(currentPageEMR)) return !syncCalOff && canScrape;
  if (usesTabScheduleSync(emr)) return false;

  const isWebPTPage =
    Boolean(currentPageEMR) && !usesTabScheduleSync(currentPageEMR);
  const isWebPT = isWebPTPage || usesProvidedScheduleSync(emr);
  const eligible = isWebPTPage || showSyncCal(emr);
  return eligible && (!isWebPT || (syncCalEnabled && canScrape));
};

/** Resolve sync eligibility + context from the user's stats (one round-trip). */
export const getSyncCalContext = async (apiClient) => {
  let emr = null;
  let syncCalEnabled = false;
  let syncCalOff = false;
  let providerHint = {};
  try {
    const stats = await apiClient.getStats({});
    emr = stats?.EMR || null;
    syncCalEnabled = stats?.features?.sync_cal === true;
    // Unset means on for Raintree/Stride/Prompt; only an explicit false turns it off.
    syncCalOff = stats?.features?.sync_cal === false;
    // Raintree shows every provider at the location, so the injector needs to
    // know which column belongs to this user.
    providerHint = {
      providerId: stats?.provider_id || '',
      providerName: stats?.name || '',
    };
  } catch (e) {
    return { available: false, emr: null, currentPageEMR: null };
  }
  let currentPageEMR = null;
  try {
    const page = await getPageUrl();
    currentPageEMR = getWebPTEMRFromUrl(page?.url);
  } catch (e) {
    currentPageEMR = null;
  }
  const platform = (() => {
    try {
      return sessionStorage.getItem('platform') || '';
    } catch (e) {
      return '';
    }
  })();
  const available = canSyncCal({
    emr,
    syncCalEnabled,
    syncCalOff,
    currentPageEMR,
    platform,
  });
  // TEMP (Sync Cal rollout): says why the button is or isn't showing.
  console.log('[CPT sync cal]', {
    emr,
    currentPageEMR,
    syncCalEnabled,
    platform,
    embedded: isEmbeddedFrame(),
    available,
  });
  return {
    available,
    emr,
    currentPageEMR,
    providerHint,
  };
};

/**
 * Pull the schedule and create the day's conversations. WebPT, Raintree,
 * Stride and Prompt scrape the EMR tab first; CORA lets the backend do it. `onSchedule`
 * fires once the EMR has been read, so the panel can show what is about to be
 * added; `only` re-sends a subset (a retry). Returns the API result plus the schedule
 * that produced it. Throws on failure.
 */
export const runScheduleSync = async (
  apiClient,
  { emr, currentPageEMR, providerHint } = {},
  { onSchedule, only } = {},
) => {
  const effectiveEMR = currentPageEMR || emr;
  let providedSchedule = {};
  let schedule = null;
  if (usesProvidedScheduleSync(effectiveEMR)) {
    schedule = await getScheduleFromEMR(providerHint || {});
    const appointments = only || schedule?.appointments || [];
    const cancelled = only ? [] : schedule?.cancelled || [];
    if (!appointments.length && !cancelled.length) {
      throw new Error('No appointments found for today.');
    }
    // Nothing to add, but the cancellations still go back to the panel.
    if (!appointments.length) {
      return {
        syncedDate: schedule?.date,
        appointments,
        cancelled,
        skipped: [],
      };
    }
    schedule = { ...schedule, appointments };
    if (onSchedule) onSchedule(schedule);
    // `skipped`/`cancelled` are ours, for the panel — the API only wants the
    // appointments.
    providedSchedule = {
      appointments,
      date: schedule.date,
      emr: effectiveEMR,
    };
  }
  const result = await apiClient.getSchedule(providedSchedule);
  if (result && 'error' in result) throw new Error(result.error);
  return {
    ...result,
    syncedDate: schedule?.date,
    appointments: schedule?.appointments || [],
    skipped: only ? [] : schedule?.skipped || [],
    cancelled: only ? [] : schedule?.cancelled || [],
  };
};
